import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Phone, MapPin, ChevronRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_content-update-22/artifacts/mejz8dgl_file_00000000c3bc622f96fe678a8b87cead.png";

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header with Logo and Phone */}
      <header className="bg-white shadow-sm py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <motion.img 
              src={LOGO_URL} 
              alt="My Roadside Hero" 
              className="h-20 md:h-28 w-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="logo"
            />
            <motion.a 
              href="tel:1300753727"
              className="flex items-center gap-3 bg-[hsl(355,85%,45%)] text-white px-6 py-3 rounded-full font-bold text-xl md:text-2xl tracking-wide hover:bg-[hsl(355,85%,38%)] transition-all hover:scale-105 shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="phone-button"
            >
              <Phone className="h-6 w-6" />
              1300 753 727
            </motion.a>
          </div>
        </div>
      </header>

      {/* Main Content - Region Selector */}
      <main className="flex-1 flex items-center justify-center py-12 md:py-20 px-4">
        <div className="w-full max-w-4xl">
          <motion.h1 
            className="font-heading text-4xl md:text-6xl font-black text-center text-[hsl(0,0%,8%)] mb-4 uppercase tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Choose Your Location
          </motion.h1>
          <motion.p 
            className="text-center text-slate-600 text-lg mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Select your region to find roadside assistance near you
          </motion.p>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Brisbane Region Card */}
            <motion.div
              className="group cursor-pointer"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              onClick={() => navigate("/brisbane")}
              data-testid="brisbane-region-card"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-200 hover:shadow-2xl hover:border-[hsl(355,85%,45%)] transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 md:h-56 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_content-update-22/artifacts/9wuqqzrl_Old_Bremer_Tafe_-_Ipswich-1300.png"
                    alt="Brisbane Region"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[hsl(0,0%,8%)] uppercase">
                        Brisbane Region
                      </h2>
                      <div className="flex items-center gap-2 mt-2 text-slate-500">
                        <MapPin className="h-4 w-4" />
                        <span className="text-sm">Ipswich, Toowoomba, Lockyer Valley</span>
                      </div>
                    </div>
                    <div className="bg-[hsl(355,85%,45%)] text-white p-3 rounded-full group-hover:bg-[hsl(0,0%,8%)] transition-colors">
                      <ChevronRight className="h-6 w-6" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Wide-Bay Region Card */}
            <motion.div
              className="group cursor-pointer"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              onClick={() => navigate("/wide-bay")}
              data-testid="widebay-region-card"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-200 hover:shadow-2xl hover:border-[hsl(355,85%,45%)] transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 md:h-56 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_content-update-22/artifacts/jbewfi7e_urangan-pier.jpg"
                    alt="Wide-Bay Region"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[hsl(0,0%,8%)] uppercase">
                        Wide-Bay Region
                      </h2>
                      <div className="flex items-center gap-2 mt-2 text-slate-500">
                        <MapPin className="h-4 w-4" />
                        <span className="text-sm">Tiaro, Maryborough, Hervey Bay, Childers, Bundaberg</span>
                      </div>
                    </div>
                    <div className="bg-[hsl(355,85%,45%)] text-white p-3 rounded-full group-hover:bg-[hsl(0,0%,8%)] transition-colors">
                      <ChevronRight className="h-6 w-6" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[hsl(0,0%,8%)] text-white py-6 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-300 text-sm">
            Available 24/7 - Call us anytime at{" "}
            <a href="tel:1300753727" className="text-[hsl(355,85%,45%)] font-semibold hover:underline" data-testid="footer-phone">
              1300 753 727
            </a>
          </p>
          <p className="text-slate-400 text-xs mt-2">
            &copy; {new Date().getFullYear()} My Roadside Hero. All rights reserved.
            {" | "}
            <a href="https://admin-referral-hub.emergent.host/admin/login" className="hover:text-slate-300 transition-colors" data-testid="admin-link">
              Admin
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
